import {useCallback, useEffect, useRef, useState} from 'react'
import Icon from '../icon'
import Popup from '../popup'
import InputMultiDateField from '../InputMultiDateField'
import {theme} from '../../theme'
import './styles.css'

interface DashboardPeriodPickerProps {
  /** Expanded, sorted list of every day in the selected span ("YYYY-MM-DD"). */
  value: string[]
  onChange: (dates: string[]) => void
  label: string
  /**
   * Opens the calendar on mount when no period is selected yet. Callers that
   * render this picker as the result of the user *switching into* period mode
   * pass it: an empty trigger gives no hint that a range must be picked, so the
   * calendar shows itself. Callers with an always-set period leave it off.
   */
  autoOpenWhenEmpty?: boolean
}

/** Formats "YYYY-MM-DD" as "DD/MM/YYYY". */
const formatFull = (date: string): string => {
  const [year, month, day] = date.split('-')
  return `${day}/${month}/${year}`
}

// Presentational: a trigger button showing the current period that opens the
// range calendar (InputMultiDateField, single contiguous span) inside a popup.
// Mirrors the calendarHeader pattern (a trigger + Popup) so the always-open
// calendar no longer takes permanent space in the dashboard header.
export default function DashboardPeriodPicker({
  value,
  onChange,
  label,
  autoOpenWhenEmpty = false,
}: DashboardPeriodPickerProps) {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number
    left: number
  } | null>(null)

  const from = value[0]
  const to = value[value.length - 1]
  const rangeText = !from
    ? ''
    : from === to
      ? formatFull(from)
      : `${formatFull(from)} – ${formatFull(to)}`

  // The popup is positioned by coordinates, not by an anchor element, so both
  // the click and the auto-open path measure the trigger the same way.
  const openPopup = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    setAnchorPosition({top: rect.y + rect.height, left: rect.x})
  }, [])

  // With no range picked the trigger shows only its label: nothing tells the
  // user a period is expected of them. Open the calendar so the next step is
  // visible instead of guessed.
  const isEmpty = value.length === 0
  useEffect(() => {
    if (!autoOpenWhenEmpty || !isEmpty) return
    openPopup()
  }, [autoOpenWhenEmpty, isEmpty, openPopup])

  return (
    <div className='dashboardPeriodPicker'>
      <div
        ref={triggerRef}
        className={`dashboardPeriodPickerTrigger${anchorPosition ? ' active' : ''}`}
        onClick={openPopup}>
        {label && <span className='dashboardPeriodPickerLabel'>{label}</span>}
        {rangeText && (
          <span className='dashboardPeriodPickerValue'>{rangeText}</span>
        )}
        <Icon
          type='calendarMonth'
          size='24px'
          color={
            anchorPosition
              ? theme.palette.primary.main
              : theme.palette.text.primary
          }
        />
      </div>
      <Popup
        anchorPosition={anchorPosition}
        setAnchorPosition={setAnchorPosition}
        isShowPadding={false}>
        <InputMultiDateField
          multiple={false}
          initialValues={value}
          onChange={onChange}
        />
      </Popup>
    </div>
  )
}
